/**
 * **AI 가 준 2×2 격자 한 장**을 걷기 프레임 4장으로 푼다 (브라우저).
 *
 * ## 왜 필요한가
 * 생성기는 **가로로 긴 4프레임 스트립을 잘 못 그린다** — 칸마다 크기가 흔들린다.
 * 그래서 2×2 격자로 받는데(docs/build/29), 지금 관리자 「움직임 만들기」는
 * **프레임 4장을 따로 올리는** 것만 받는다. 운영자는 격자를 손으로 자를 방법이 없다.
 *
 * ## 배치 계산은 여기서 하지 않는다
 * 크기·발끝 위치는 `packages/shared/src/walkStrip.ts` 가 낸다 —
 * 개발자용 `scripts/grid-to-walk-strip.cjs` 와 **같은 숫자**를 써야 한다.
 * 각자 짜면 관리자에서 만든 것과 개발자가 만든 것이 미묘하게 달라지고,
 * 그 방향만 키가 다르거나 발이 뜬다(CLAUDE.md).
 *
 * 여기서 하는 것은 **캔버스로 자르고·배경 지우고·그려 넣는 일**뿐이다.
 */
import { cutoutBackground, inspectBackground, opaqueBounds, gridCells, planWalkStrip, type Pixels } from "@greg-chick/shared";

/**
 * 웹 규약 — 걷기 프레임 한 칸(기준 띠가 없을 때).
 * 🔴 2026-09-18 에 128 → 256. 화면을 꽉 채우며 확대하자 128px 칸은 폰·맥북에서 늘려 그려져 흐렸다
 * (`pnpm check:artres`). 형제가 있으면 **형제 크기**를 따른다 — 부르는 쪽이 `frame` 을 넘긴다.
 */
export const FRAME = 256;

export interface GridStripResult {
  /** 완성된 스트립(칸 frame px × 4). 그대로 올리면 된다. */
  blob: Blob;
  dataUrl: string;
  /** 칸마다 키가 얼마나 달랐나(0~1). 화면에 보여 준다. */
  wobble: number;
}

function ctxOf(w: number, h: number) {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("캔버스를 쓸 수 없어요");
  return { c, ctx };
}

/** 한 칸을 잘라 배경을 지우고, 인물만 남긴 캔버스와 크기를 돌려준다. */
function cutCell(
  img: HTMLImageElement,
  cell: { left: number; top: number; width: number; height: number },
) {
  const { c, ctx } = ctxOf(cell.width, cell.height);
  ctx.drawImage(img, cell.left, cell.top, cell.width, cell.height, 0, 0, cell.width, cell.height);
  const id = ctx.getImageData(0, 0, cell.width, cell.height);
  const px: Pixels = { data: id.data, width: cell.width, height: cell.height };

  // 생성물은 흰 배경으로 온다. 이미 투명하면 건드리지 않는다(두 번 지우면 경계가 깎인다).
  if (inspectBackground(px).hasBackground) cutoutBackground(px);
  const box = opaqueBounds(px);
  if (!box) throw new Error("칸에서 인물을 못 찾았어요 — 격자가 2×2 가 맞는지 봐 주세요");

  ctx.putImageData(id, 0, 0);
  const w = box.right - box.left + 1;
  const h = box.bottom - box.top + 1;
  const cropped = ctxOf(w, h);
  cropped.ctx.drawImage(c, box.left, box.top, w, h, 0, 0, w, h);
  return { canvas: cropped.c, width: w, height: h };
}

/**
 * 기준 띠에서 잰 몸 높이·발 여백을 **이번 칸 크기로** 옮긴다.
 * 128칸 띠를 재서 256칸으로 구우면서 그대로 쓰면 몸이 **절반 크기**로 들어간다.
 */
export function refForFrame(
  ref: { bodyH: number; footMargin: number; frame?: number },
  frame: number,
): { bodyH: number; footMargin: number } {
  const k = ref.frame ? frame / ref.frame : 1;
  return { bodyH: Math.round(ref.bodyH * k), footMargin: Math.round(ref.footMargin * k) };
}

/**
 * 2×2 격자 → 걷기 스트립(칸 `frame` px × 4).
 *
 * @param ref 기존 스트립에서 잰 값. 있으면 **그 키·발 위치를 그대로** 쓴다 —
 *            없으면 그 방향만 키가 10% 작아진다. `ref.frame` 이 이번 칸과 다르면 비율로 옮긴다.
 * @param lockHeight 생성물이면 true. 칸마다 흔들린 키를 하나로 맞춘다.
 * @param frame 칸 크기. **형제 크기**(`familyFrameSize`)를 넘길 것 — 안 넘기면 FRAME.
 *              여기서 작게 만들고 게시할 때 형제 크기로 늘리면 흐려진다(2026-09-18).
 */
export async function gridToStrip(
  img: HTMLImageElement,
  opts: { ref?: { bodyH: number; footMargin: number; frame?: number }; lockHeight?: boolean; frame?: number } = {},
): Promise<GridStripResult> {
  const cells = gridCells(img.naturalWidth, img.naturalHeight);
  const cut = cells.map((cell) => cutCell(img, cell));
  const frame = opts.frame ?? FRAME;
  const ref = opts.ref ? refForFrame(opts.ref, frame) : undefined;

  const plan = planWalkStrip({
    cells: cut.map((c) => ({ width: c.width, height: c.height })),
    frame,
    ...(ref ? { ref } : {}),
    lockHeight: opts.lockHeight ?? true, // 관리자에 올라오는 건 대개 생성물이다
  });

  const out = ctxOf(plan.stripWidth, plan.stripHeight);
  out.ctx.imageSmoothingEnabled = true;
  out.ctx.imageSmoothingQuality = "high";
  plan.frames.forEach((f, i) => {
    out.ctx.drawImage(cut[i]!.canvas, f.left, f.top, f.width, f.height);
  });

  const blob = await new Promise<Blob | null>((r) => out.c.toBlob(r, "image/webp", 0.92));
  if (!blob) throw new Error("스트립을 만들지 못했어요");
  return { blob, dataUrl: out.c.toDataURL("image/webp", 0.92), wobble: plan.wobble };
}

/**
 * 기존 스트립에서 **몸 높이와 발 여백**을 잰다 — 새로 만드는 것을 여기에 맞춘다.
 * 캐릭터군마다 프레임을 채우는 비율이 다르다(히어로 룩 0.97 · 마을 친구 0.88).
 */
export async function measureRef(url: string): Promise<{ bodyH: number; footMargin: number; frame: number } | null> {
  const img = await new Promise<HTMLImageElement | null>((res) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => res(i);
    i.onerror = () => res(null);
    i.src = url;
  });
  if (!img || !img.naturalWidth) return null;
  const h = img.naturalHeight;
  const { c, ctx } = ctxOf(h, h); // 첫 프레임만
  ctx.drawImage(img, 0, 0, h, h, 0, 0, h, h);
  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, h, h).data;
  } catch {
    return null; // 다른 출처라 픽셀을 못 읽는다 — 막지 말고 기본값으로 간다
  }
  const box = opaqueBounds({ data, width: h, height: h });
  void c;
  // `frame` 은 잰 칸 크기 — 다른 칸 크기로 구울 때 비율로 옮기려고 같이 돌려준다
  return box ? { bodyH: box.bottom - box.top + 1, footMargin: h - 1 - box.bottom, frame: h } : null;
}
